export const WEFAT_MODAL_API_NAMES = Object.freeze(['showModal']);
export class WxModalRuntimeError extends Error {
    api = 'showModal';
    code;
    errCode;
    errMsg;
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `showModal:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxModalRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([
    ...CALLBACK_KEYS,
    'cancelColor',
    'cancelText',
    'confirmColor',
    'confirmText',
    'content',
    'editable',
    'placeholderText',
    'showCancel',
    'title',
]);
const HEX_COLOR = /^#[0-9a-fA-F]{6}$/;
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxModalRuntimeError('WF6191', reason, 'Pass documented showModal options with direct data properties, valid colors, short button labels, and callable callbacks.');
}
function environmentError(reason, cause) {
    if (cause instanceof WxModalRuntimeError)
        return cause;
    return new WxModalRuntimeError('WF6190', reason, 'Run the generated application in a browser with HTMLDialogElement support after document.body is available.', cause);
}
function stringOption(values, key, fallback, maxLength) {
    const value = values[key] ?? fallback;
    if (typeof value !== 'string' || [...value].length > maxLength) {
        throw optionError(`${key} must be a string of at most ${maxLength} characters`);
    }
    return value;
}
function buttonText(values, key, fallback) {
    const value = stringOption(values, key, fallback, 4);
    if (value.length === 0)
        throw optionError(`${key} must not be empty`);
    return value;
}
function colorOption(values, key, fallback) {
    const value = values[key] ?? fallback;
    if (typeof value !== 'string' || !HEX_COLOR.test(value)) {
        throw optionError(`${key} must be a six-digit hex color`);
    }
    return value.toLowerCase();
}
function booleanOption(values, key, fallback) {
    const value = values[key] ?? fallback;
    if (typeof value !== 'boolean')
        throw optionError(`${key} must be a boolean when provided`);
    return value;
}
function readPresentation(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    const title = stringOption(raw, 'title', undefined, 1_024);
    const content = stringOption(raw, 'content', undefined, 16_384);
    if (title.length === 0 && content.length === 0) {
        throw optionError('title and content must not both be empty');
    }
    return {
        callbacks: raw,
        presentation: Object.freeze({
            cancelColor: colorOption(raw, 'cancelColor', '#000000'),
            cancelText: buttonText(raw, 'cancelText', '\u53d6\u6d88'),
            confirmColor: colorOption(raw, 'confirmColor', '#576b95'),
            confirmText: buttonText(raw, 'confirmText', '\u786e\u5b9a'),
            content,
            editable: booleanOption(raw, 'editable', false),
            placeholderText: stringOption(raw, 'placeholderText', '', 1_024),
            showCancel: booleanOption(raw, 'showCancel', true),
            title,
        }),
    };
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'showModal', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
function browserShowModal(presentation) {
    if (typeof HTMLDialogElement === 'undefined' || typeof HTMLDialogElement.prototype.showModal !== 'function') {
        return Promise.reject(environmentError('HTML dialog support is unavailable'));
    }
    const host = document.querySelector('[data-wefat-app]') ?? document.body;
    if (host === null)
        return Promise.reject(environmentError('document.body is unavailable'));
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const dialog = document.createElement('dialog');
    dialog.className = 'wefat-modal';
    dialog.setAttribute('data-wefat-feedback', 'showModal');
    dialog.setAttribute('aria-labelledby', 'wefat-modal-title');
    dialog.setAttribute('aria-describedby', 'wefat-modal-content');
    const title = document.createElement('h2');
    title.id = 'wefat-modal-title';
    title.className = 'wefat-modal__title';
    title.textContent = presentation.title;
    const content = document.createElement('p');
    content.id = 'wefat-modal-content';
    content.className = 'wefat-modal__content';
    content.textContent = presentation.content;
    const input = presentation.editable ? document.createElement('input') : null;
    if (input !== null) {
        input.className = 'wefat-modal__input';
        input.placeholder = presentation.placeholderText;
        input.setAttribute('aria-label', presentation.placeholderText || presentation.title);
    }
    const actions = document.createElement('div');
    actions.className = 'wefat-modal__actions';
    const cancel = document.createElement('button');
    cancel.className = 'wefat-modal__button wefat-modal__button--cancel';
    cancel.type = 'button';
    cancel.textContent = presentation.cancelText;
    cancel.style.color = presentation.cancelColor;
    const confirm = document.createElement('button');
    confirm.autofocus = true;
    confirm.className = 'wefat-modal__button wefat-modal__button--confirm';
    confirm.type = 'button';
    confirm.textContent = presentation.confirmText;
    confirm.style.color = presentation.confirmColor;
    if (presentation.showCancel)
        actions.appendChild(cancel);
    actions.appendChild(confirm);
    dialog.append(title, content);
    if (input !== null)
        dialog.appendChild(input);
    dialog.appendChild(actions);
    return new Promise((resolve, reject) => {
        let settled = false;
        const finish = (confirmValue) => {
            if (settled)
                return;
            settled = true;
            dialog.close();
            dialog.remove();
            previousFocus?.focus();
            resolve(Object.freeze({
                cancel: !confirmValue,
                confirm: confirmValue,
                content: input?.value ?? '',
            }));
        };
        cancel.addEventListener('click', () => finish(false));
        confirm.addEventListener('click', () => finish(true));
        dialog.addEventListener('cancel', (event) => {
            event.preventDefault();
            if (presentation.showCancel)
                finish(false);
        });
        try {
            host.appendChild(dialog);
            dialog.showModal();
        }
        catch (error) {
            settled = true;
            dialog.remove();
            previousFocus?.focus();
            reject(environmentError('could not display the browser dialog', error));
        }
    });
}
function browserEnvironment() {
    if (typeof document === 'undefined')
        return null;
    return { showModal: browserShowModal };
}
export function createBrowserModalWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const environment = options.environment ?? browserEnvironment();
    if (environment === null || !isPlainRecord(environment) || typeof environment.showModal !== 'function') {
        throw new TypeError('createBrowserModalWxApis requires a browser document or injected environment');
    }
    return Object.freeze({
        showModal(raw) {
            const { callbacks, presentation } = readPresentation(raw);
            const task = Promise.resolve()
                .then(() => environment.showModal(presentation))
                .then((decision) => Object.freeze({
                cancel: decision.cancel,
                confirm: decision.confirm,
                content: decision.content,
                errMsg: 'showModal:ok',
            }))
                .catch((error) => {
                throw environmentError(error instanceof Error ? error.message : 'browser modal failed', error);
            });
            if (callbacks.success === undefined && callbacks.fail === undefined && callbacks.complete === undefined) {
                return task;
            }
            void task.then((result) => {
                invokeCallback(callbacks.success, result, 'success', options.onCallbackError);
                invokeCallback(callbacks.complete, result, 'complete', options.onCallbackError);
            }, (error) => {
                invokeCallback(callbacks.fail, error, 'fail', options.onCallbackError);
                invokeCallback(callbacks.complete, error, 'complete', options.onCallbackError);
            });
            return undefined;
        },
    });
}
//# sourceMappingURL=modal-adapter.js.map